import React, { useEffect, useRef, useState } from "react";

/** Fixed pseudo-random dust specks (stable between renders). */
const DUST = Array.from({ length: 18 }, (_, i) => {
  const r = (n: number) => ((Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1 + 1) % 1;
  return { left: 45 + r(1) * 53, top: 35 + r(2) * 60, dur: 5 + r(3) * 4, delay: r(4) * 6 };
});

/**
 * "Живая главная" (desktop only): the hero photo slowly zooms, a beam of light
 * passes, dust floats, and — if the admin marked the cup — steam rises from it.
 * The photo is laid out exactly like `background-size: cover`, so the steam
 * point (percent of the photo) lands on the cup at any window width.
 */
export function LiveHeroPhoto({ src, steam }: { src: string; steam?: { x: number; y: number } | null }) {
  const box = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [frame, setFrame] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setNatural({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setFrame({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  let stage: React.CSSProperties = { inset: 0 };
  if (natural && frame && frame.w > 0) {
    const scale = Math.max(frame.w / natural.w, frame.h / natural.h);
    const w = natural.w * scale;
    const h = natural.h * scale;
    stage = { left: (frame.w - w) / 2, top: (frame.h - h) / 2, width: w, height: h };
  }
  const origin = steam ? `${steam.x}% ${steam.y}%` : "70% 60%";

  return (
    <div ref={box} className="dh-hero__photo dh-live" aria-hidden="true">
      <div className="dh-live__stage" style={{ ...stage, backgroundImage: `url(${src})`, transformOrigin: origin }}>
        {steam && natural && (
          <div className="dh-live__steam" style={{ left: `${steam.x}%`, top: `${steam.y}%` }}>
            <i />
            <i />
            <i />
            <i />
          </div>
        )}
      </div>
      <div className="dh-live__glow" />
      <div className="dh-live__rays" />
      <div className="dh-live__dust">
        {DUST.map((d, i) => (
          <b key={i} style={{ left: `${d.left}%`, top: `${d.top}%`, animationDuration: `${d.dur.toFixed(1)}s`, animationDelay: `${d.delay.toFixed(1)}s` }} />
        ))}
      </div>
    </div>
  );
}
